import { ForbiddenUserInfo } from '@bmwe/data';
import { createContext, ReactNode, useContext } from 'react';
import { useUser } from './user';
import {
  getActiveUserAppRole,
  isRoleSufficient,
  PermissionSet,
  ROLE_HIERARCHY,
} from '../helper';
import { ActiveUser, RoleAssignmentModel } from '../types';

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  isForbidden: boolean;
  forbiddenUserInfo?: ForbiddenUserInfo;
  user: ActiveUser | null;
  hasPermission: (permission: PermissionSet) => boolean;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { user, isLoading, isForbidden, forbiddenUserInfo } = useUser();

  const appRole = getActiveUserAppRole(user);

  const hasPermission = (permission: PermissionSet): boolean => {
    if (!user) {
      return false;
    }

    const allowedRoles = Array.isArray(permission.allowedRoles)
      ? permission.allowedRoles
      : [permission.allowedRoles];

    const checkAndEvaluateRoles = (
      roleAssignments: RoleAssignmentModel[],
      forObjectType: boolean,
    ): boolean | undefined => {
      const matchingAssignments = roleAssignments.filter((ra) => {
        if (forObjectType) {
          // Dossier-scoped assignments only apply to checks for that dossier —
          // they must not shadow object-wide checks (e.g. "may create dossiers").
          return (
            ra.objectType === permission.objectType &&
            (permission.dossierId
              ? ra.dossierId === permission.dossierId
              : !ra.dossierId)
          );
        } else {
          return !ra.objectType && !ra.dossierId;
        }
      });

      if (matchingAssignments.length > 0) {
        const highestRole = matchingAssignments.sort(
          (a, b) => ROLE_HIERARCHY[b.roleType] - ROLE_HIERARCHY[a.roleType],
        )[0];

        return allowedRoles.some((requiredRole) =>
          isRoleSufficient(highestRole.roleType, requiredRole),
        );
      }
      return undefined; // No matching roles found
    };

    const allGroupRoles = user.groups?.flatMap((g) => g.roleAssignments) ?? [];

    // When checking object-specific permissions, ALWAYS evaluate specific roles first
    // before falling back to app-level roles (specificity wins over hierarchy)
    if (permission.objectType) {
      // 1. User's direct object-specific role assignments (highest priority for same specificity)
      const userObjectResult = checkAndEvaluateRoles(
        user.roleAssignments,
        true,
      );
      if (userObjectResult !== undefined) {
        return userObjectResult;
      }

      // 2. Group roles (Object-Specific) (second priority for same specificity)
      const groupObjectResult = checkAndEvaluateRoles(allGroupRoles, true);
      if (groupObjectResult !== undefined) {
        return groupObjectResult;
      }

      // Only fall back to app-level roles if no object-specific roles exist
    }

    // 3. User's direct app-level role assignments
    if (appRole) {
      if (checkAndEvaluateRoles([appRole], false)) {
        return true;
      }
    }

    // 4. Group roles (App-Level)
    const groupAppResult = checkAndEvaluateRoles(allGroupRoles, false);
    return !!groupAppResult;
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user,
        user,
        isLoading,
        isForbidden,
        forbiddenUserInfo,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
